import {fetchPoll, fetchSafeDocumentFragment} from '@github-ui/fetch-utils'
import {
  getCheckedElements,
  toggleBulkActionMenus,
  updateSelectedNotificationsInBulkActionForms,
} from './notifications/v2/bulk-action-dropdowns'
import {getCurrentFocus, restoreFocus} from './notifications/v2/notification-list-focus'
import {getUpdateId, invalidateUpdates, updateNotifications} from './notifications/v2/update-notifications'
import type {Kicker} from '@github-ui/remote-form'
import {changeValue} from '@github-ui/form-utils'

import {announce} from '@github-ui/aria-live'
import {fromEvent} from '@github-ui/subscription'
import {isShortcutAllowed} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import {observe} from '@github/selector-observer'
import {on} from 'delegated-events'
import {remoteForm} from '@github-ui/remote-form'
import {updateNotificationStates} from './notifications/v2/update-notification-states'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'
import type {QueryBuilderElement} from '@github-ui/query-builder-element'
import {getBaseFetchHeaders} from '@github-ui/fetch-headers'

// When true, per-checkbox change handlers should avoid running the expensive
// updateSelectedNotificationsInBulkActionForms() and let the mark-all handler
// trigger a single batched update instead.
let isSuppressingIndividualHandlers = false

observe('.js-bulk-action-toasts[data-job-in-progress]', () => {
  displayBulkProcessingToast()
})

async function displayBulkProcessingToast(alreadyProcessing = false) {
  const jobUrl = document.querySelector<HTMLElement>('.js-bulk-action-toasts')!.getAttribute('data-job-url')
  if (!jobUrl) throw new Error('could not get job url')

  // We will display an alternative message if the user tries to submit a request
  // when they already have a pending or running job.
  if (alreadyProcessing) {
    showBulkActionToast('.js-bulk-actions-already-processing-toast')
  } else {
    showBulkActionToast('.js-bulk-actions-processing-toast')
  }

  try {
    const pollResponse = await fetchPoll(jobUrl, {headers: {accept: 'application/json'}})
    if (pollResponse.status === 200) {
      showBulkActionToast('.js-bulk-actions-succeeded-toast')
      updateNotifications()
    }
  } catch {
    showBulkActionToast('.js-bulk-actions-failed-toast')
  }
}

function textBannersAnnounce(classname: string) {
  const messages = {
    'already-processing': `We're still processing your last request. Please wait for that to complete before submitting a new request`,
    'actions-processing': `Bulk actions currently being processed`,
    succeeded: `Bulk action processing complete`,
    failed: `Bulk action processing failed, please try again`,
    unsupported: `Bulk actions are not currently supported for this query`,
  }

  const bannerKey = Object.keys(messages).find(k => classname.includes(k))
  return bannerKey ? messages[bannerKey as keyof typeof messages] : ''
}

function showBulkActionToast(classname: string) {
  const target = document.querySelector<HTMLElement>('.js-notifications-banners-target')
  const template = document.querySelector<HTMLTemplateElement>(classname)
  const text = textBannersAnnounce(classname)

  if (target && template) {
    target.innerHTML = template.innerHTML
    if (text) {
      announce(text)
    }
  }
}

// Show the prompt to select _everything_ if all the things on the current
// page are selected
on('change', '.js-notifications-mark-all-prompt', function (event) {
  const checked = (event.currentTarget as HTMLInputElement).checked

  // Suppress individual checkbox change handlers while performing a bulk select/unselect.
  isSuppressingIndividualHandlers = true

  // defer to stop flash if it changes before hiding
  setTimeout(function () {
    // Re-assert suppression inside the callback to be defensive against other
    // code that might mutate the flag between scheduling and execution.
    isSuppressingIndividualHandlers = true

    try {
      const elements = document.querySelectorAll<HTMLElement>('.js-notifications-mark-all')

      for (const markAllText of elements) {
        if (markAllText) {
          markAllText.hidden = !checked
        }
      }

      // After bulk operation completes, perform the expensive update once and
      // stop suppressing individual change handlers.
      updateSelectedNotificationsInBulkActionForms()
    } finally {
      isSuppressingIndividualHandlers = false
    }
  }, 0)
})

// Show the mark all dropdown when the "Select all..." text is clicked
on('click', '.js-notifications-mark-all', function () {
  toggleBulkActionMenus({markSelected: false, markAll: true})
})

// Clear the selection when the "Clear selection" text is clicked
on('click', '.js-notifications-unmark-all', function () {
  changeValue(document.querySelector<HTMLInputElement>('.js-notifications-mark-all-prompt')!, false)
})

// Replace blank slate with notification list.
observe('.js-notifications-blankslate', {
  subscribe: el =>
    fromEvent(el, 'socket:message', async function (event: Event) {
      const url = (event.currentTarget as Element).getAttribute('data-url')
      if (!url) throw new Error('could not get url')
      const response = await fetch(url, {headers: {Accept: 'application/json', ...getBaseFetchHeaders()}})
      if (!response.ok) {
        return
      }
      const json: {notifications_exist: boolean} = await response.json()
      if (json.notifications_exist) {
        updateNotifications()
      }
    }),
})

// If the user navigates with turbo, immediately invalidate any updateNotification tasks
// that may be happening, as they will definitely be incorrect
document.addEventListener(SOFT_NAV_STATE.SUCCESS, function () {
  invalidateUpdates()
})

async function performRequest(wants: Kicker) {
  try {
    await wants.text()
  } catch {
    // TODO show error message
  }
}

// Perform an action, change the notification status, and optionally update the page
remoteForm('.js-notification-action form', async function (form: HTMLFormElement, wants) {
  const shouldUpdate = form.hasAttribute('data-update')
  const bulkAction = form.closest('.js-notification-bulk-action') != null
  const shouldRedirect = form.hasAttribute('data-redirect-to-inbox-on-submit')
  const shouldCloseOnboardingElement = form.hasAttribute('data-close-onboarding-element')

  if (shouldRedirect) {
    await performRequest(wants)
    const inboxButton = document.querySelector<HTMLAnchorElement>('.js-notifications-back-to-inbox')!

    if (inboxButton) {
      inboxButton.click()
    }

    return
  }

  // Generate a unique update ID so that we correctly invalidate existing updates that might
  // happen during performRequest
  const updateId = getUpdateId()

  if (bulkAction) {
    const currentFocus = getCurrentFocus()

    // When performing a bulk action we may programmatically uncheck many items.
    // Suppress per-checkbox change handlers while we do this so that
    // updateSelectedNotificationsInBulkActionForms() is only called once.
    isSuppressingIndividualHandlers = true

    try {
      for (const checked of getCheckedElements()) {
        updateNotificationStates(checked, form)

        // Uncheck items if we are going to perform an update so the hidden items
        // can be replaced successfully on update. hasInteractions will return
        // true if the checked state does not match the default value which
        // will prevent the replacement.
        if (shouldUpdate) {
          changeValue(checked, false)
        }
      }
    } finally {
      // End suppressing per-checkbox handlers.
      isSuppressingIndividualHandlers = false
    }

    restoreFocus(currentFocus)

    try {
      const response = await wants.text()
      if (response.status === 202) {
        displayBulkProcessingToast()
        if (shouldCloseOnboardingElement) {
          document.querySelector<HTMLElement>('.js-onboarding-dialog')!.removeAttribute('open')
          document.querySelector<HTMLElement>('.js-dismiss-notification-onboarding-notice')!.click()
        }
      }
    } catch (error) {
      // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
      if (error.response.status === 403) {
        showBulkActionToast('.js-bulk-actions-unsupported-query-toast')
        // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
      } else if (error.response.status === 409) {
        displayBulkProcessingToast(true)
      }
    }
  } else {
    const currentFocus = getCurrentFocus()
    updateNotificationStates(form, form)
    restoreFocus(currentFocus)
    await performRequest(wants)
  }

  if (shouldUpdate) {
    updateNotifications(updateId)
  }
})

// Remove notification items when snooze action is submitted
remoteForm('.notification-action-snooze form', async function (form: HTMLFormElement, wants) {
  await performRequest(wants)

  const notification = form.closest<HTMLElement>('[data-notification-id]')
  if (notification) {
    notification.classList.add('notification-archived')
  }
})

// Show bulk actions when an item checked, hide when all items are unchecked
on('change', '.js-notification-bulk-action-check-item', function () {
  // If we're currently performing a bulk select/unselect, skip per-checkbox
  // updates to avoid executing expensive work multiple times. The mark-all
  // handler will call updateSelectedNotificationsInBulkActionForms() once.
  if (isSuppressingIndividualHandlers) return

  updateSelectedNotificationsInBulkActionForms()
})

// Toggle the notification item checkbox when 'x' is pressed
on('navigation:keydown', '.js-notifications-list-item', function (event) {
  if (!isShortcutAllowed(event.detail.originalEvent)) return

  if (event.detail.hotkey === 'x') {
    const checkbox = event.currentTarget.querySelector<HTMLInputElement>('.js-notification-bulk-action-check-item')!
    changeValue(checkbox, !checkbox.checked)
    event.preventDefault()
    event.stopPropagation()
  }
})

// Show form that allows you to create a new inbox.
on('click', '.js-custom-inbox-create-button', event => {
  const el = event.currentTarget as HTMLButtonElement

  const detailsDialog = detailsDialogFromElement(el)
  const details = detailsDialog.closest<HTMLElement>('.js-custom-inbox-details')!

  let searchTerm = ''
  if (details.classList.contains('js-save-search-details')) {
    // The user loaded the dialog using the "Save" button next to the search
    // input, so always use the search input value as the default query for
    // which the user is creating a filter. This helps provide a good default
    // query after the user deletes a filter in response to the max filters flash.
    searchTerm = readSearchInput()
  }

  displayCreateNewInbox(detailsDialog, searchTerm)
})

// Submit forms for bulk actions in grouped view  asynchronously and update the view accordingly
remoteForm('.js-grouped-notifications-mark-all-read-button', async function (form: HTMLFormElement, wants) {
  const submitButton = form.querySelector<HTMLButtonElement>('button')!
  submitButton.disabled = true

  showBulkActionToast('.js-bulk-actions-processing-toast')

  try {
    const response = await wants.text()
    if (response.status === 200) {
      showBulkActionToast('.js-bulk-actions-succeeded-toast')

      // Add notification-archived class to notifications in group
      const notificationItems = form
        .closest<HTMLElement>('.js-notifications-group')!
        .querySelectorAll('.js-notifications-list-item')
      for (const notification of notificationItems) {
        notification.classList.add('notification-archived')
      }
    }
  } catch (error) {
    submitButton.disabled = false
    // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
    if (error.response.status === 503) {
      showBulkActionToast('.js-bulk-actions-failed-toast')
    }
  }
})

// Submit forms in the custom inbox dialog asynchronously and update the dialog contents and custom inboxes in the sidebar
remoteForm('.js-custom-inbox-form', async function (form: HTMLFormElement, wants) {
  try {
    await wants.json()
  } catch {
    return
  }

  const detailsDialog = detailsDialogFromElement(form)
  updateNotifications()
  updateDialogContent(detailsDialog)
})

observe('.js-custom-inbox-details', details => {
  details.addEventListener('toggle', () => {
    const detailsDialog = details.querySelector<HTMLElement>('.js-custom-inbox-dialog')!

    if (!details.hasAttribute('open')) {
      resetDialogContent(detailsDialog)
      return
    }

    // if it's not the save a new search button, don't pass a query string
    if (!details.classList.contains('js-save-search-details')) {
      updateDialogContent(detailsDialog)
      return
    }

    const searchInput = readSearchInput()
    updateDialogContent(detailsDialog, searchInput)
  })
})

function readSearchInput(): string {
  const queryInput = document.querySelector<HTMLInputElement>('#notifications-search-input')

  if (queryInput) {
    return queryInput.value
  }
  return ''
}

async function updateDialogContent(detailsDialog: Element, newInboxQueryString = '') {
  const url = detailsDialog.getAttribute('src')
  if (!url) throw new Error('could not get src')

  let html
  try {
    html = await fetchSafeDocumentFragment(document, url)
  } catch {
    return
  }

  const dialogContent = detailsDialog.querySelector<HTMLElement>('.js-custom-inbox-dialog-content')!

  // Hide the spinner
  detailsDialog.querySelector<HTMLElement>('.js-custom-inbox-dialog-spinner')!.hidden = true
  // Replace dialog content with response
  dialogContent.textContent = ''
  dialogContent.appendChild(html)

  const maxFiltersFlash = document.querySelector<HTMLElement>('.js-custom-inbox-max-filters-flash')

  if (newInboxQueryString.length > 0 && maxFiltersFlash) {
    // If the user is trying to save via the "save search" button
    // but already has the max amount of filters, display the max filters flash.
    maxFiltersFlash.hidden = false
  } else if (newInboxQueryString.length > 0) {
    // If the user is trying to save via the "save search" button and does not
    // already have the max filters, display the create inbox fields.
    displayCreateNewInbox(detailsDialog, newInboxQueryString)
  }
}

function displayCreateNewInbox(detailsDialog: Element, newInboxQueryString = '') {
  // Set the new query string input value to the search input value
  if (newInboxQueryString.length > 0) {
    const queryInput = detailsDialog.querySelector<HTMLInputElement>('.js-custom-inbox-query-string-input')!
    queryInput.value = newInboxQueryString
  }

  const container = detailsDialog.querySelector<HTMLElement>('.js-custom-inbox-create-form-container')!
  const nameInput = container.querySelector<HTMLElement>('.js-custom-inbox-name-input')!
  const createButton = detailsDialog.querySelector<HTMLButtonElement>('.js-custom-inbox-create-button')!

  createButton.disabled = true
  container.hidden = false
  nameInput.focus()
}

function resetDialogContent(detailsDialog: Element) {
  detailsDialog.querySelector<HTMLElement>('.js-custom-inbox-dialog-spinner')!.hidden = false
  detailsDialog.querySelector<HTMLElement>('.js-custom-inbox-dialog-content')!.textContent = ''
}

function detailsDialogFromElement(el: Element) {
  const details = el.closest<HTMLElement>('.js-custom-inbox-details')!
  return details.querySelector<HTMLElement>('.js-custom-inbox-dialog')!
}

observe('.js-notifications-group .notification-archived', {
  constructor: HTMLElement,
  add: updateGroupDisplay,
})

function updateGroupDisplay(notification: HTMLElement) {
  const group = notification.closest<HTMLElement>('.js-notifications-group')!

  const notificationElements = notification.parentElement?.children
  if (!notificationElements) return

  const allNotificationsArchived = Array.from(notificationElements).every(el =>
    el.classList.contains('notification-archived'),
  )
  const groupDoesNotHaveViewMoreLink = group.querySelector('.js-notifications-group-view-all') == null

  // Only remove the group if all notifications are archived AND the group does not have a link to view more notifications
  if (allNotificationsArchived && groupDoesNotHaveViewMoreLink) group.remove()
}

on('query-builder:clear', '#query-builder-notifications-search-input', function (event) {
  const query = new URLSearchParams(window.location.search).get('query')

  // Default query builder behaviour
  if (!query) {
    return true
  }

  // Override query builder and submit the cleared form
  event.preventDefault()

  const queryBuilder = event.target as QueryBuilderElement
  const searchInput = queryBuilder.input

  queryBuilder.clearInput({focusInput: false}) // prevent the autocomplete panel from opening

  const form = searchInput.closest('form')
  form?.submit()

  return false
})
